/**
 * Skeleton matching ProjectCard's shape exactly:
 *   card-sm container → aspect-[3/2] thumbnail → icon+title row → meta row
 *   (updated, issue count, avatar stack).
 *
 * Height parity is critical — when real data lands, layout must not shift.
 * If ProjectCard's structure changes, update both files in the same PR.
 */
interface ProjectCardSkeletonProps {
  /** 0-indexed position used to stagger the shimmer so the grid breathes
   *  instead of pulsing in lockstep. */
  index?: number;
}

export function ProjectCardSkeleton({ index = 0 }: ProjectCardSkeletonProps) {
  const delay = (slot: number) => ({ animationDelay: `${index * 80 + slot * 40}ms` });

  return (
    <article className="card-sm relative" aria-hidden="true">
      <div className="block">
        {/* Thumbnail — matches aspect-[3/2] from ProjectCard */}
        <div
          className="aspect-[3/2] rounded-lg skeleton-shimmer mb-3 border border-gray-200"
          style={delay(0)}
        />

        {/* Info row: icon + title + dots. h-5 = ProjectCard's title row
            (measured 2026-09-18: without it the skeleton ran 4px short). */}
        <div className="flex items-center gap-2 min-w-0 h-5">
          <div className="w-4 h-4 rounded skeleton-shimmer shrink-0" style={delay(1)} />
          <div className="h-4 rounded skeleton-shimmer flex-1" style={delay(2)} />
          <div className="w-4 h-4 rounded skeleton-shimmer shrink-0" style={delay(3)} />
        </div>

        {/* Meta row: updated left; issue count + avatar right (min-h-6 = the avatars) */}
        <div className="flex items-center justify-between gap-3 mt-3 min-h-6">
          <div className="h-3 rounded w-20 skeleton-shimmer" style={delay(4)} />
          <div className="flex items-center gap-3 shrink-0">
            <div className="h-3 rounded w-8 skeleton-shimmer" style={delay(5)} />
            <div className="w-6 h-6 rounded-full skeleton-shimmer" style={delay(6)} />
          </div>
        </div>
      </div>
    </article>
  );
}
